<template>
  <div class="article-list">
    <div class="flex gap-5 justify-between items-end mb-6 mdmax:flex-col">
      <div class="flex w-full gap-5 mdmax:w-full mdmax:flex-col">
        <div class="w-full max-w-lg mdmax:w-full">
          <label
            class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >Pencarian</label
          >
          <div class="relative">
            <TextField
              type="text"
              :withIcon="true"
              placeholder="Cari artikel..."
              v-model="search"
              @enter="onSearch"
            />
            <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Search />
            </div>
          </div>
        </div>
      </div>
      <div>
        <button
            @click="$router.push('/article/create')"
            class="px-2 py-2 w-32 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
        >
            + Add Artikel
        </button>
      </div>
    </div>
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-neutral-50 text-neutral-500 text-sm font-semibold border-b border-neutral-200">
              <th class="p-4">No</th>
              <th class="p-4">Judul</th>
              <th class="p-4">Deskripsi Singkat</th>
              <th class="p-4">Tanggal Dibuat</th>
              <th class="p-4">Status</th>
              <th class="p-4">Action</th>
            </tr>
          </thead>
          <tbody class="text-neutral-700 text-sm">
            <tr
              v-for="(article, index) in items"
              :key="article.id"
              class="border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
            >
              <td class="p-4">{{ (page - 1) * limit + index + 1 }}</td>
              <td class="p-4 font-medium">{{ article.title }}</td>
              <td class="p-4 w-1/3">
                <div class="truncate max-w-xs" :title="article.sort_description">
                  {{ article.sort_description }}
                </div>
              </td>
              <td class="p-4">
                {{ formatDate(article.created_at) }}
              </td>
              <td class="p-4">
                <span
                  class="px-2 py-1 rounded text-xs font-medium capitalize"
                  :class="statusClass(article.status)"
                >
                  {{ article.status }}
                </span>
              </td>
              <td class="p-4">
                <div class="flex items-center gap-3">
                    <button @click="onEdit(article)" class="text-neutral-500 hover:text-primary-500 transition-colors">
                        <Pencil width="18" height="18" />
                    </button>
                    <button @click="onDelete(article)" class="text-neutral-500 hover:text-red-500 transition-colors">
                        <Trash width="18" height="18" />
                    </button>
                </div>
              </td>
            </tr>
            <tr v-if="!items || items.length === 0">
              <td colspan="6" class="p-4 text-center text-neutral-500">
                Data tidak ditemukan!
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      class="text-neutral-500 font-medium mt-2 text-sm"
      v-if="!isMobile && items.length > 0"
    >
      Menampilkan {{ (page - 1) * limit + 1 }} - {{ Math.min(page * limit, meta.total_item) }} dari {{ meta.total_item }}
    </div>
    <div
      class="mt-8 flex justify-end items-center mb-6"
      v-if="items.length > 0"
    >
      <pagination
        :currentPage="page"
        :totalCount="meta.total_item"
        :pageSize="limit"
        @onPageChange="changePage"
      />
    </div>

    <Modal :modalVisible="deleteModalVisible" @close="closeDeleteModal" width="400px">
        <div slot="modal-content" class="text-center">
            <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Artikel</h3>
            <p class="text-neutral-600 mb-6">Apakah Anda yakin ingin menghapus artikel <strong>{{ articleToDelete ? articleToDelete.title : '' }}</strong>?</p>
            <div class="flex justify-center gap-3">
                <button 
                    @click="closeDeleteModal"
                    class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
                >
                    Batal
                </button>
                 <button 
                    @click="confirmDelete"
                    class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
                >
                    Hapus
                </button>
            </div>
        </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import dayjs from "dayjs"

export default {
    name: 'ArticleList',
    components: {
        TextField: () => import('@/components/Forms/TextField'),
        Pagination: () => import('@/components/Pagination/Pagination'),
        Search: () => import('@/components/Icons/Search'),
        Pencil: () => import('@/components/Icons/Pencil'),
        Trash: () => import('@/components/Icons/Trash'),
        Modal: () => import('@/components/Modal/Modal')
    },
    data: () => ({
        search: '',
        page: 1,
        limit: 10,
        timer: null,
        deleteModalVisible: false,
        articleToDelete: null
    }),
    computed: {
        ...mapGetters("article", ["articles"]),
        ...mapGetters("common", ["isMobile"]),
        meta() {
            // Adjust based on the actual store state structure. 
            // Assuming store.articles is { data: [...], meta: {...} }
            return this.articles.meta || { total_page: 1, total_item: 0 }
        },
        items() {
            // Assuming store.articles is { data: [...], meta: {...} }
            return this.articles.data || []
        }
    },
    methods: {
        ...mapActions("article", ["getArticleList", "deleteArticle"]),
        ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),
        
        formatDate(date) {
            if (!date) return "-"
            const rawDate = typeof date === 'string' ? date.replace('Z', '') : date
            return dayjs(rawDate).format("DD MMM YYYY")
        },
        statusClass(status) {
            if (status === 'Active') return 'bg-green-100 text-green-700'
            return 'bg-gray-100 text-gray-700'
        },
        fetchArticles() {
            this.showLoading()
            const params = {
                page: this.page,
                per_page: this.limit,
                search_keyword: this.search
            }
            this.getArticleList(params)
                .catch(err => console.error(err))
                .finally(() => this.hideLoading())
        },
        onSearch() {
            if (this.timer) clearTimeout(this.timer)
            this.timer = setTimeout(() => {
                this.page = 1
                this.fetchArticles()
            }, 500)
        },
        changePage(val) {
            this.page = val
            this.fetchArticles()
        },
        onEdit(article) {
            this.$router.push(`/article/${article.id}`)
        },
        onDelete(article) {
            this.articleToDelete = article
            this.deleteModalVisible = true
        },
        closeDeleteModal() {
            this.deleteModalVisible = false
            this.articleToDelete = null
        },
        async confirmDelete() {
            if (!this.articleToDelete) return

            this.showLoading()
            try {
                await this.deleteArticle(this.articleToDelete.id)
                this.showExactToast({
                    message: "Artikel berhasil dihapus",
                    type: "success"
                })
                this.fetchArticles()
            } catch (error) {
                console.error(error)
                this.showExactToast({
                    message: error.response?.data?.message || "Gagal menghapus artikel",
                    type: "error"
                })
            } finally {
                this.hideLoading()
                this.closeDeleteModal()
            }
        }
    },
    mounted() {
        this.fetchArticles()
    }
}
</script>

<style lang="scss" scoped>
/* Scoped styles if needed, currently none derived fromPromo besides general structure */
</style>
